import {
  CameraIcon,
  FileCsvIcon,
  FileIcon,
  FilePdfIcon,
  FileXlsIcon,
  FolderOpenIcon,
  type Icon,
  ImageBrokenIcon,
  SpinnerGapIcon,
  TrashIcon,
  XIcon,
} from '@phosphor-icons/react'
import { useEffect, useRef, useState } from 'react'

import { AvisoError } from '@/components/Form'
import { HojaInferior } from '@/components/HojaInferior'
import { ApiError } from '@/lib/api'
import {
  ACCEPT_TODO,
  type Adjunto,
  type AdjuntoVisible,
  type DestinoAdjunto,
  esImagen,
  formatearTamano,
  guardarAdjunto,
  motivoDeRechazo,
  useVistaPrevia,
} from '@/lib/adjuntos'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { useBorrarAdjunto, useSubirAdjunto } from '@/lib/queries'

/**
 * Comprobantes de gasto y evidencias de revisión (§23).
 *
 * La foto del ticket sale del teléfono, en la caja, con una mano ocupada. Por
 * eso el botón principal lleva `capture="environment"`: abre la cámara trasera
 * de golpe, sin pasar por el selector de archivos. El segundo botón —sin
 * `capture`— existe porque en iOS ese atributo **quita** el acceso a la galería,
 * y un ticket fotografiado hace dos horas dejaría de poder adjuntarse.
 *
 * Los PDF no se previsualizan a propósito. Renderizar uno en el cliente cuesta
 * cientos de kilobytes de dependencia, y un estado de cuenta no se reconoce por
 * su miniatura sino por su nombre: la ficha con nombre y tamaño informa igual y
 * pesa cero.
 *
 * El componente es **controlado**: recibe la lista y avisa de los cambios. Los
 * dos sitios donde se usa guardan esa lista en distinto lugar —la captura en
 * estado local, porque el movimiento acaba de nacer; la revisión en la propia
 * consulta, donde el servidor ya devuelve su evidencia— y un estado interno
 * habría acabado discrepando de uno de los dos.
 */

/** Icono de un archivo que no se puede mirar de un vistazo. */
const ICONO_POR_TIPO: Readonly<Record<string, Icon>> = {
  'application/pdf': FilePdfIcon,
  'text/csv': FileCsvIcon,
  'application/vnd.ms-excel': FileXlsIcon,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': FileXlsIcon,
}

/** Cómo se llama en pantalla lo que se adjunta. */
const ETIQUETA_TIPO: Readonly<Record<string, string>> = {
  'image/jpeg': 'Foto JPG',
  'image/png': 'Imagen PNG',
  'image/webp': 'Imagen WebP',
  'application/pdf': 'PDF',
  'text/csv': 'CSV',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
}

export function Comprobante({
  destino,
  adjuntos,
  onSubido,
  onBorrado,
  modo = 'ticket',
  soloLectura = false,
}: {
  destino: DestinoAdjunto
  adjuntos: readonly AdjuntoVisible[]
  onSubido?: (adjunto: Adjunto) => void
  onBorrado?: (id: string) => void
  /**
   * Consultar sin poder tocar. Lo usa el historial: una revisión cerrada es un
   * registro de lo que se encontró aquel día, y borrarle la evidencia después
   * es reescribir la prueba.
   */
  soloLectura?: boolean
  /**
   * `ticket` es el comprobante de un gasto: uno solo, foto o PDF.
   * `evidencia` es el respaldo de una revisión: varios, y además hojas de
   * cálculo —el estado de cuenta del banco baja en Excel más veces que en PDF.
   */
  modo?: 'ticket' | 'evidencia'
}) {
  const subir = useSubirAdjunto()
  const borrar = useBorrarAdjunto()

  const [rechazo, setRechazo] = useState<string | null>(null)
  const [viendo, setViendo] = useState<AdjuntoVisible | null>(null)
  const [porBorrar, setPorBorrar] = useState<AdjuntoVisible | null>(null)

  const camara = useRef<HTMLInputElement>(null)
  const archivos = useRef<HTMLInputElement>(null)

  const esRevision = 'reviewId' in destino
  const unoSolo = modo === 'ticket'
  const lleno = soloLectura || (unoSolo && adjuntos.length >= 1)

  const elegir = (input: HTMLInputElement): void => {
    const file = input.files?.[0]
    // El valor se limpia siempre, incluso al cancelar: sin esto, elegir dos
    // veces el mismo archivo no dispara el segundo `change` y parece que la app
    // ignoró el toque.
    input.value = ''
    if (!file) return

    // Se valida antes de subir para no gastar 9 MB de datos móviles en una foto
    // que el servidor va a rechazar en el último byte, y para poder decir qué
    // pasa con este archivo en vez de un «error al subir».
    const motivo = motivoDeRechazo(file)
    if (motivo) {
      haptic('error')
      setRechazo(motivo)
      return
    }

    setRechazo(null)
    subir.mutate(
      { file, ...destino },
      {
        onSuccess: (adjunto) => {
          haptic('success')
          onSubido?.(adjunto)
        },
        onError: () => haptic('error'),
      },
    )
  }

  const confirmarBorrado = (): void => {
    if (!porBorrar || borrar.isPending) return
    const id = porBorrar.id
    borrar.mutate(
      { id, eraDeRevision: esRevision },
      {
        onSuccess: () => {
          haptic('success')
          setPorBorrar(null)
          onBorrado?.(id)
        },
        onError: () => haptic('error'),
      },
    )
  }

  const aviso =
    rechazo ??
    (subir.error ? mensajeDeError(subir.error, 'No pudimos subir el archivo.') : null)

  return (
    <section className="flex flex-col gap-2.5">
      {adjuntos.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {adjuntos.map((adjunto) => (
            <li key={adjunto.id}>
              <FilaAdjunto
                adjunto={adjunto}
                onAbrir={() => {
                  haptic('light')
                  if (esImagen(adjunto.mimeType)) {
                    setViendo(adjunto)
                    return
                  }
                  // Un PDF o un Excel no se mira: se guarda. Es la única acción
                  // que tiene sentido ofrecer sobre él desde el teléfono.
                  void guardarAdjunto(adjunto).catch(() => haptic('error'))
                }}
                onBorrar={
                  soloLectura
                    ? null
                    : () => {
                        haptic('light')
                        setPorBorrar(adjunto)
                      }
                }
              />
            </li>
          ))}
        </ul>
      ) : null}

      <AvisoError mensaje={aviso} />

      {lleno ? null : (
        <div className="flex gap-2">
          {/* `accept` y `capture` van en el input, no en el botón: el botón solo
              lo dispara para poder darle el tamaño y el aspecto del resto de la
              app —un `<input type="file">` nativo no se puede peinar. */}
          <input
            ref={camara}
            type="file"
            accept="image/*,application/pdf"
            capture="environment"
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            onChange={(e) => elegir(e.currentTarget)}
          />
          <input
            ref={archivos}
            type="file"
            accept={modo === 'evidencia' ? ACCEPT_TODO : 'image/*,application/pdf'}
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            onChange={(e) => elegir(e.currentTarget)}
          />

          <BotonAdjuntar
            principal
            icono={CameraIcon}
            ocupado={subir.isPending}
            onClick={() => camara.current?.click()}
          >
            {modo === 'evidencia' ? 'Tomar foto' : 'Adjuntar comprobante'}
          </BotonAdjuntar>

          <BotonAdjuntar
            icono={FolderOpenIcon}
            ocupado={false}
            deshabilitado={subir.isPending}
            onClick={() => archivos.current?.click()}
            aria-label="Elegir un archivo del dispositivo"
          >
            Archivo
          </BotonAdjuntar>
        </div>
      )}

      {soloLectura ? (
        adjuntos.length === 0 ? (
          <p className="text-[13px] leading-snug text-ink-soft">
            Esta revisión se cerró sin adjuntar nada. El total quedó registrado igual.
          </p>
        ) : null
      ) : (
        <p className="text-[12px] leading-snug text-ink-faint">
          {modo === 'evidencia'
            ? 'Foto, PDF o Excel del saldo real. Máximo 10 MB por archivo.'
            : 'La foto del ticket o el PDF de la factura. Máximo 10 MB.'}
        </p>
      )}

      {viendo ? <VistaCompleta adjunto={viendo} onCerrar={() => setViendo(null)} /> : null}

      <HojaInferior
        abierta={porBorrar !== null}
        titulo="¿Borrar este archivo?"
        descripcion={
          porBorrar
            ? `«${porBorrar.fileName}» se elimina del servidor y no se puede recuperar.`
            : undefined
        }
        onCerrar={() => {
          if (!borrar.isPending) setPorBorrar(null)
        }}
      >
        <AvisoError
          mensaje={borrar.error ? mensajeDeError(borrar.error, 'No pudimos borrarlo.') : null}
        />
        <div className="flex flex-col gap-2 pt-1">
          <button
            type="button"
            onClick={confirmarBorrado}
            disabled={borrar.isPending}
            className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-card bg-expense text-[15px] font-semibold text-white active:scale-[0.98] disabled:opacity-60"
          >
            {borrar.isPending ? (
              <SpinnerGapIcon size={19} className="animate-spin" />
            ) : (
              <TrashIcon size={18} weight="fill" />
            )}
            Sí, borrarlo
          </button>
          <button
            type="button"
            onClick={() => setPorBorrar(null)}
            disabled={borrar.isPending}
            className="flex min-h-[52px] w-full items-center justify-center rounded-card border border-line bg-surface text-[15px] font-semibold text-ink active:scale-[0.98]"
          >
            Conservarlo
          </button>
        </div>
      </HojaInferior>
    </section>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Piezas
// ═══════════════════════════════════════════════════════════════════════════

function BotonAdjuntar({
  icono: Icono,
  children,
  onClick,
  ocupado,
  principal = false,
  deshabilitado = false,
  ...resto
}: {
  icono: Icon
  children: React.ReactNode
  onClick: () => void
  ocupado: boolean
  principal?: boolean
  deshabilitado?: boolean
  'aria-label'?: string
}) {
  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        onClick()
      }}
      disabled={ocupado || deshabilitado}
      className={cn(
        'flex min-h-[48px] items-center justify-center gap-2 rounded-card px-4 text-[14px] font-semibold',
        'transition-transform duration-150 active:scale-[0.98] disabled:opacity-60',
        principal
          ? 'flex-1 bg-accion text-on-accion'
          : 'shrink-0 border border-line bg-surface text-ink',
      )}
      {...resto}
    >
      {ocupado ? (
        <SpinnerGapIcon size={18} className="animate-spin" />
      ) : (
        <Icono size={18} weight="fill" />
      )}
      {ocupado ? 'Subiendo…' : children}
    </button>
  )
}

function FilaAdjunto({
  adjunto,
  onAbrir,
  onBorrar,
}: {
  adjunto: AdjuntoVisible
  onAbrir: () => void
  /** `null` en solo lectura: no hay botón, en vez de uno que no hace nada. */
  onBorrar: (() => void) | null
}) {
  const imagen = esImagen(adjunto.mimeType)
  const tipo = ETIQUETA_TIPO[adjunto.mimeType] ?? 'Archivo'

  return (
    <div className="flex items-center gap-3 rounded-card border border-line bg-surface p-2">
      {/* Dos botones hermanos y no uno dentro de otro: anidar botones es HTML
          inválido y los lectores de pantalla se pierden el interior. */}
      <button
        type="button"
        onClick={onAbrir}
        className="flex min-h-[56px] min-w-0 flex-1 items-center gap-3 rounded-card text-left active:scale-[0.99]"
        aria-label={imagen ? `Ver ${adjunto.fileName}` : `Guardar ${adjunto.fileName}`}
      >
        <Miniatura adjunto={adjunto} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-medium text-ink">
            {adjunto.fileName}
          </span>
          <span className="block truncate text-[12px] text-ink-soft">
            {tipo} · {formatearTamano(adjunto.sizeBytes)} ·{' '}
            {imagen ? 'Toca para verlo' : 'Toca para guardarlo'}
          </span>
        </span>
      </button>

      {onBorrar ? (
        <button
          type="button"
          onClick={onBorrar}
          aria-label={`Borrar ${adjunto.fileName}`}
          className="flex size-11 shrink-0 items-center justify-center rounded-full text-ink-faint active:scale-95"
        >
          <TrashIcon size={19} weight="fill" />
        </button>
      ) : null}
    </div>
  )
}

/**
 * Miniatura de un adjunto.
 *
 * Solo las imágenes se descargan: con `null` el hook no pide nada, así que la
 * ficha de un PDF no gasta ni una petición. Cada miniatura tiene su propio
 * `objectURL` y lo revoca al desmontarse — no se comparte con la vista completa
 * a propósito, porque entonces habría que decidir cuál de las dos lo revoca y
 * esa es exactamente la clase de duda que acaba en una fuga.
 */
function Miniatura({ adjunto }: { adjunto: AdjuntoVisible }) {
  const imagen = esImagen(adjunto.mimeType)
  const { url, cargando, error } = useVistaPrevia(imagen ? adjunto.id : null)
  const Icono = ICONO_POR_TIPO[adjunto.mimeType] ?? FileIcon

  if (!imagen) {
    return (
      <span className="flex size-14 shrink-0 items-center justify-center rounded-card bg-surface-sunken text-ink-soft">
        <Icono size={24} weight="fill" />
      </span>
    )
  }

  return (
    <span className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-card bg-surface-sunken text-ink-faint">
      {url ? (
        <img src={url} alt="" className="size-full object-cover" />
      ) : cargando ? (
        <SpinnerGapIcon size={18} className="animate-spin" />
      ) : (
        <ImageBrokenIcon size={20} weight="fill" aria-label={error ?? undefined} />
      )}
    </span>
  )
}

/**
 * La imagen a pantalla completa.
 *
 * Vive en su propio componente para que su `objectURL` muera con él: al cerrar
 * la vista se desmonta, el hook revoca y el blob se libera. Si el visor fuera un
 * bloque condicional dentro del componente padre, la URL sobreviviría a cada
 * apertura y la app iría acumulando fotos en memoria.
 */
function VistaCompleta({ adjunto, onCerrar }: { adjunto: AdjuntoVisible; onCerrar: () => void }) {
  const { url, cargando, error } = useVistaPrevia(adjunto.id)

  useEffect(() => {
    const alTeclear = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCerrar()
    }
    window.addEventListener('keydown', alTeclear)
    const previo = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', alTeclear)
      document.body.style.overflow = previo
    }
  }, [onCerrar])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={adjunto.fileName}
      // Fondo casi negro y no el velo de siempre: una foto de ticket se lee por
      // contraste, y cualquier superficie clara alrededor se lo come.
      className="fixed inset-0 z-[60] flex flex-col bg-black/95"
    >
      <header className="px-safe flex items-center gap-3 pb-2 pt-safe">
        <p className="min-w-0 flex-1 truncate text-[14px] font-medium text-white/90">
          {adjunto.fileName}
        </p>
        <button
          type="button"
          onClick={() => {
            haptic('light')
            onCerrar()
          }}
          aria-label="Cerrar"
          className="flex size-11 shrink-0 items-center justify-center rounded-full bg-white/15 text-white active:scale-95"
        >
          <XIcon size={19} weight="fill" />
        </button>
      </header>

      <div className="flex flex-1 items-center justify-center overflow-hidden px-3 pb-safe">
        {url ? (
          <img src={url} alt={adjunto.fileName} className="max-h-full max-w-full object-contain" />
        ) : cargando ? (
          <SpinnerGapIcon size={30} className="animate-spin text-white/70" />
        ) : (
          <p className="px-8 text-center text-[14px] leading-snug text-white/80">
            {error ?? 'No pudimos abrir el archivo.'}
          </p>
        )}
      </div>
    </div>
  )
}

/** El detalle por campo que manda el servidor dice qué corregir; un genérico no. */
function mensajeDeError(error: unknown, respaldo: string): string {
  if (error instanceof ApiError) {
    const detalles = Object.values(error.fieldErrors ?? {}).flat()
    return detalles.length > 0 ? detalles.join('. ') : error.message
  }
  return respaldo
}
